import { DateFiled, cn } from "@steelyard/theme";
import type { ComponentProps } from "react";

/**
 * Every calendar picker in this app should go through here, not `@steelyard/theme`'s `DateFiled`
 * directly. react-datepicker's own stylesheet puts its popup at `z-index: 1` — lower than the
 * `z-10` every `FormFooter` sticks to the bottom of the viewport with (`packages/theme/src/
 * components/form-footer.tsx`, a copied file this app can't edit). A date field low enough on a
 * form for its calendar to open near the bottom of the screen then renders *under* that sticky
 * bar, which doesn't hide the popup so much as slice a week's row out of it. `!z-50` (Tailwind's
 * `!important` modifier — react-datepicker's own z-index isn't an inline style, but `!` makes the
 * override unconditional regardless of stylesheet order) keeps the calendar above anything else
 * on the page.
 *
 * `DateFiled` also hard-codes `w-72` (288px), wider than a phone's form column; `max-w-full` lets
 * it shrink there instead of pushing the whole page into a horizontal scroll.
 *
 * `required` appends the same visible `text-red` asterisk `Field`/`SelectField` render after a
 * required label — `DateFiled`'s own `inputProps.label` accepts a `ReactNode` (rizzui's `Input`),
 * so no separate component is needed for this.
 */
export function DateField({ required, ...props }: ComponentProps<typeof DateFiled> & { required?: boolean }) {
  // `DateFiled` hard-codes a 36px (`h-9`) input; every other control here — a filter's select or
  // search box, a form's field — is rizzui's 40px default, as in the Marketplace admin portal.
  const inputProps = { ...props.inputProps, inputClassName: cn("h-10 [&_input]:text-ellipsis", props.inputProps?.inputClassName) };
  return (
    <DateFiled
      {...props}
      inputProps={
        required
          ? {
              ...inputProps,
              label: (
                <>
                  {inputProps.label}
                  <span aria-hidden="true" className="text-red">
                    {" "}
                    *
                  </span>
                </>
              ),
            }
          : inputProps
      }
      className={cn("w-72 max-w-full", props.className)}
      popperClassName={cn("!z-50", props.popperClassName)}
    />
  );
}
